/**
 * Client de l'API de matching — L5.
 *
 * **Un seul chemin de ce module est cachable par le service worker** : `/lifestyle/traits`, qui est
 * un référentiel public et identique pour tout le monde. Tous les autres portent un score, une
 * réponse ou une identité — un seul champ personnalisé suffit à rendre une réponse incachable, et
 * l'oublier la ferait resservir à un autre compte après une reconnexion (invariant 17).
 *
 * **Aucune explication n'arrive en clair.** Le serveur rend un `explanationCode` et une charge
 * utile ; la phrase est composée ici, en français ou en arabe. Un texte tout fait côté serveur
 * serait un cul-de-sac d'internationalisation, et l'arabe de L9 devrait reprendre chaque ligne déjà
 * écrite (modèle §8.2, invariant 11).
 */

import { appeler } from './api'

export type MatchKind = 'SeekerToListing' | 'SeekerToHousehold' | 'SeekerToSeeker'
export type MatchStatus = 'New' | 'Seen' | 'Contacted' | 'Dismissed' | 'Applied'
export type Axe = 'Budget' | 'Location' | 'Schedule' | 'Lifestyle' | 'Reputation' | 'Features'
export type Echelle = 'Ordinal5' | 'Boolean' | 'Enumerated'

/**
 * Comment se compare un trait — **et ça change la question posée**.
 *
 * `Directional` : « quel niveau maximum acceptez-vous chez l'autre ? »
 * `Divergence` : « quel écart acceptez-vous ? »
 *
 * Poser la même question pour les deux rendrait les réponses inexploitables sans qu'aucune erreur
 * ne le signale — la propreté n'est pas le bruit, c'est l'écart qui use.
 */
export type Comparaison = 'Directional' | 'Divergence'

export type Importance = 'Indifferent' | 'NiceToHave' | 'Important' | 'Dealbreaker'
export type NiveauLangue = 'Notions' | 'Conversational' | 'Fluent' | 'Native'
export type MixiteFoyer = 'Mixed' | 'WomenOnly' | 'MenOnly'

export interface Trait {
  code: string
  category: string
  scale: Echelle
  comparison: Comparaison
  isEliminatory: boolean
  appliesToSelf: boolean
  appliesToTolerance: boolean
  appliesToListing: boolean
  displayOrder: number
  label: string
}

export interface Reponse {
  traitCode: string
  selfValue: number | null
  toleranceValue: number | null
  importance: Importance
}

export interface LangueParlee {
  languageCode: string
  level: NiveauLangue
}

export interface ProfilLifestyle {
  exists: boolean
  completedAt: string | null
  version: number
  desiredGenderPolicy: MixiteFoyer | null
  answers: Reponse[]
  languages: LangueParlee[]
}

export interface DeclarationAnnonce {
  traitCode: string
  practiceValue: number | null
  toleranceValue: number | null
}

export interface AxeExplique {
  axis: Axe
  rawScore: number
  /** Poids **effectif**, redistribution comprise : c'est lui qui s'affiche. */
  weight: number
  weightedScore: number
  explanationCode: string
  explanationData: Record<string, unknown>
}

export interface Match {
  id: string
  kind: MatchKind
  listingId: string | null
  listingTitle: string | null
  coverMediaId: string | null
  rentAllInclusive: number | null
  district: string | null
  targetUserId: string | null
  targetDisplayName: string | null
  score: number
  status: MatchStatus
  computedAt: string
  axes: AxeExplique[]
}

export interface PageMatchs {
  items: Match[]
  page: number
  size: number
  total: number
}

export interface Explication {
  id: string | null
  kind: MatchKind
  score: number
  isEliminated: boolean
  eliminationRuleCode: string | null
  scoringProfileVersion: number
  computedAt: string
  axes: AxeExplique[]
}

export interface Candidat {
  id: string
  seekerUserId: string
  seekerDisplayName: string | null
  score: number
  status: MatchStatus
  computedAt: string
  hasApplied: boolean
  axes: AxeExplique[]
}

export interface PageCandidats {
  items: Candidat[]
  page: number
  size: number
  total: number
}

export function traits(langue: string, pourAnnonce = false): Promise<Trait[]> {
  return appeler<Trait[]>(`/lifestyle/traits?language=${langue}&listingOnly=${pourAnnonce}`)
}

export function monLifestyle(): Promise<ProfilLifestyle> {
  return appeler<ProfilLifestyle>('/me/lifestyle')
}

export function enregistrerLifestyle(
  desiredGenderPolicy: MixiteFoyer | null,
  answers: Reponse[],
): Promise<{ id: string }> {
  return appeler<{ id: string }>('/me/lifestyle', {
    method: 'PUT',
    body: JSON.stringify({ desiredGenderPolicy, answers }),
  })
}

export function enregistrerLangues(languages: LangueParlee[]): Promise<void> {
  return appeler<void>('/me/languages', { method: 'PUT', body: JSON.stringify({ languages }) })
}

export function declarationAnnonce(annonceId: string): Promise<DeclarationAnnonce[]> {
  return appeler<DeclarationAnnonce[]>(`/listings/${annonceId}/lifestyle`)
}

export function enregistrerDeclarationAnnonce(
  annonceId: string,
  traits: DeclarationAnnonce[],
): Promise<void> {
  return appeler<void>(`/listings/${annonceId}/lifestyle`, {
    method: 'PUT',
    body: JSON.stringify({ traits }),
  })
}

export function mesMatchs(page = 1, size = 20): Promise<PageMatchs> {
  return appeler<PageMatchs>(`/me/matches?page=${page}&size=${size}`)
}

export function explicationDuMatch(id: string): Promise<Explication> {
  return appeler<Explication>(`/me/matches/${id}`)
}

export function marquerVu(id: string): Promise<void> {
  return appeler<void>(`/me/matches/${id}/seen`, { method: 'POST' })
}

export function ecarter(id: string, reasonCode?: string): Promise<void> {
  return appeler<void>(`/me/matches/${id}/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ reasonCode: reasonCode ?? null }),
  })
}

export function candidatsRecommandes(annonceId: string, page = 1): Promise<PageCandidats> {
  return appeler<PageCandidats>(`/listings/${annonceId}/candidates?page=${page}&size=20`)
}

/** « Pourquoi cette annonce n'apparaît pas » — recalculé, rien n'est écrit côté serveur. */
export function expliquerAnnonce(annonceId: string): Promise<Explication> {
  return appeler<Explication>(`/listings/${annonceId}/match`)
}

/** Une annonce du listing qui est dans le fil de la personne connectée — ticket `fo-recherche-16`. */
export interface MatchAnnonce {
  listingId: string
  matchId: string
  score: number
}

/** Le plafond du serveur — `GetListingMatchesHandler.MaxListingIds`. */
export const MATCHS_PAR_APPEL = 50

/**
 * Parmi des annonces affichées, celles qui sont dans le fil « Recommandé pour vous ».
 *
 * **Appelé APRÈS le listing, jamais à sa place** : `/search` reste anonyme et identique pour tout
 * le monde, l'étiquette arrive ensuite et se pose sur des cartes déjà là. Cinquante identifiants
 * au plus par appel — une page de résultats, pas un catalogue. Personnel, donc jamais mis en
 * cache par le service worker (invariant 17).
 */
export function matchsPourAnnonces(ids: readonly string[]): Promise<MatchAnnonce[]> {
  const params = new URLSearchParams()

  for (const id of ids) {
    params.append('ids', id)
  }

  return appeler<MatchAnnonce[]>(`/me/matches/listings?${params}`)
}
